import { useMemo } from "react";
import { SwapHeader } from "./swap-header";
import { SwapCardLayout } from "./swap-card-layout";
import { SwapSendFooter } from "./swap-send-footer";
import { useSwapUpload } from "@/lib/hooks/use-swap-upload";
import { useHubStore } from "../use-hub-store";
import { MentorshipDetails } from "@/lib/api/mentorships";
import {
  getMenteeDisplayName,
  getMenteeDisplayPhoto,
} from "@/lib/redacted-display";
import { hubToast } from "../hub-toasts";
import {
  useSwapPresignedUrl,
  useCreateSwap,
  type SchedulePayload,
} from "@/lib/api/shareable-entities";
import axios from "axios";
import { createThumbnail } from "@/lib/utils/image";
import { usePostHogEvent } from "@/lib/posthog";
import {
  SWAP_PROMPTS,
  type SwapPrompt,
} from "@somethings-ui/core/src/swap-prompts";
import { ScheduleFooterButton } from "../scheduling/schedule-footer-button";

interface UploadSwapViewProps {
  mentorship: MentorshipDetails;
}

export function UploadSwapView({ mentorship }: UploadSwapViewProps) {
  const draftSwap = useHubStore((state) => state.draftSwap);
  const setDraftSwap = useHubStore((state) => state.setDraftSwap);
  const navigateToView = useHubStore((state) => state.navigateToView);
  const goBack = useHubStore((state) => state.goBack);

  const prompt = useMemo(
    () => SWAP_PROMPTS.find((p: SwapPrompt) => p.id === draftSwap?.promptId),
    [draftSwap?.promptId],
  );

  const upload = useSwapUpload(draftSwap?.photo);
  const presignedUrlMutation = useSwapPresignedUrl();
  const createSwapMutation = useCreateSwap();
  const { dispatchEvent } = usePostHogEvent("mentor_hub", { onMount: false });

  const uploadAndCreateSwap = async (schedule?: SchedulePayload) => {
    if (!draftSwap || !upload.photo) return;

    upload.setIsUploading(true);

    const [presignedData, thumbnail] = await Promise.all([
      presignedUrlMutation.mutateAsync({
        filename: upload.photo.file.name,
        contentType: upload.photo.file.type,
      }),
      createThumbnail(upload.photo.file),
    ]);

    await Promise.all([
      axios.put(presignedData.highQuality.url, upload.photo.file, {
        headers: { "Content-Type": upload.photo.file.type },
      }),
      axios.put(presignedData.thumbnail.url, thumbnail, {
        headers: { "Content-Type": thumbnail.type },
      }),
    ]);

    await createSwapMutation.mutateAsync({
      promptId: draftSwap.promptId,
      mentorshipId: mentorship.id,
      image: {
        key: presignedData.highQuality.key,
        url: presignedData.highQuality.url.split("?")[0],
        thumbnailKey: presignedData.thumbnail.key,
        thumbnailUrl: presignedData.thumbnail.url.split("?")[0],
      },
      schedule,
    });
  };

  const handleSend = async () => {
    try {
      await uploadAndCreateSwap();
      dispatchEvent("mentor_hub_swap_shared");
      hubToast.sent("swap");
      setDraftSwap(null);
      navigateToView("list");
    } catch (error) {
      console.error("Failed to send swap:", error);
      hubToast.error("Failed to send swap. Please try again.");
    } finally {
      upload.setIsUploading(false);
      upload.setIsSendDialogOpen(false);
    }
  };

  const handleSchedule = async (scheduledAt: Date) => {
    try {
      await uploadAndCreateSwap({ scheduleAt: scheduledAt.toISOString() });
      hubToast.scheduled("swap");
      setDraftSwap(null);
      navigateToView("list");
    } catch (error) {
      console.error("Failed to schedule swap:", error);
      hubToast.error("Failed to schedule swap. Please try again.");
    } finally {
      upload.setIsUploading(false);
    }
  };

  if (!draftSwap) {
    goBack();
    return null;
  }

  const onPhotoAdded = () => dispatchEvent("mentor_hub_swap_photo_added");

  return (
    <>
      <div className="flex h-full flex-col bg-white">
        <SwapHeader
          title="Swaps"
          subtitle="Create a swap for you and your teen"
          showBackButton
          showInfoButton
        />

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-base font-medium text-gray-900">
              {prompt?.title}
            </p>
            <p className="text-sm text-gray-500">{prompt?.subtitle}</p>
          </div>

          <SwapCardLayout
            mentorAvatarUrl={mentorship.mentor.profile_picture_url ?? undefined}
            mentorName={mentorship.mentor.name ?? ""}
            menteeAvatarUrl={getMenteeDisplayPhoto(mentorship.mentee)}
            menteeName={getMenteeDisplayName(mentorship.mentee)}
            photo={upload.photo}
            isDragging={upload.isDragging}
            isConverting={upload.isConverting}
            isUploading={upload.isUploading}
            onFileSelect={(files) => upload.handleFileSelect(files, onPhotoAdded)}
            onRemovePhoto={upload.handleRemovePhoto}
            onDragOver={upload.handleDragOver}
            onDragLeave={upload.handleDragLeave}
            onDrop={(e) => upload.handleDrop(e, onPhotoAdded)}
          />
        </div>

        <div className="space-y-3 border-t border-gray-200 bg-white p-4">
          <ScheduleFooterButton
            entityType="swap"
            disabled={!upload.photo || upload.isUploading}
            loading={upload.isUploading}
            onSchedule={handleSchedule}
          />
          <SwapSendFooter
            canSend={!!upload.photo}
            isUploading={upload.isUploading}
            isSendDialogOpen={upload.isSendDialogOpen}
            onOpenSendDialog={() => upload.setIsSendDialogOpen(true)}
            onCloseSendDialog={() => upload.setIsSendDialogOpen(false)}
            onConfirmSend={handleSend}
          />
        </div>
      </div>
    </>
  );
}
